import { Component, ElementRef, Input } from "@angular/core";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { VisionCategory } from "@common/ADAPT.Common.Model/organisation/vision-category";
import { ImplementationKitService } from "@common/implementation-kit/implementation-kit.service";
import { ImplementationKitArticle } from "@common/implementation-kit/implementation-kit-article.enum";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { SortUtilities } from "@common/lib/utilities/sort-utilities";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { IConfirmationDialogData } from "@common/ux/adapt-common-dialog/confirmation-dialog.component/confirmation-dialog.component";
import { EditSimpleValueBreezeEntityDialogComponent, IEditSimpleValueBreezeEntityDialogData, SimpleValueType } from "@common/ux/edit-simple-value-breeze-entity-dialog/edit-simple-value-breeze-entity-dialog.component";
import { VisionService } from "@org-common/lib/vision/vision.service";
import { ItemReorderedEvent } from "devextreme/ui/list";
import { filter, finalize, forkJoin, lastValueFrom, map, switchMap, tap } from "rxjs";
import { ConfigureFeatureBase } from "../../base-components/configure-feature-base";
import { IConfigItem } from "../../configuration.interfaces";
import { ConfigurationService } from "../../configuration.service";

@Component({
    selector: "adapt-configure-team-vision",
    standalone: false,
    templateUrl: "./configure-team-vision.component.html",
    styleUrls: ["./configure-team-vision.component.scss"],
})
export class ConfigureTeamVisionComponent extends ConfigureFeatureBase {
    @Input() public configItem?: IConfigItem;
    @Input() public team!: Team;

    public visionCategories?: VisionCategory[];
    public categoryCollapsed: { [catId: number]: boolean } = {};
    public descriptionSlugs: { [slugId: string]: string } = {};

    public constructor(
        configurationService: ConfigurationService,
        private visionService: VisionService,
        private dialogService: AdaptCommonDialogService,
        private implementationKitService: ImplementationKitService,
        elementRef: ElementRef,
    ) {
        super(configurationService, elementRef);
    }

    public async initialiseData() {
        if (this.team) {
            this.visionCategories = await lastValueFrom(this.visionService.getVisionCategoriesForTeam(this.team.teamId));
            this.visionCategories.forEach((c) => this.categoryCollapsed[c.visionCategoryId] = true);
            forkJoin(this.visionCategories
                .filter((c) => c.descriptionSlug && !c.description)
                .map((c) => this.implementationKitService.getArticle(c.descriptionSlug as ImplementationKitArticle)),
            ).pipe(
                this.takeUntilDestroyed(),
                finalize(() => this.isInitialised = true)
            ).subscribe((articles) => articles.forEach((a) => this.descriptionSlugs[String(a.id)] = a.answer));
        }

        return Promise.resolve();
    }

    public getCategoryDescription(category: VisionCategory) {
        if (category.description) {
            return category.description;
        } else if (category.descriptionSlug && this.descriptionSlugs[category.descriptionSlug]) {
            return this.descriptionSlugs[category.descriptionSlug];
        } else {
            return "";
        }
    }

    public reorderCategories(e: ItemReorderedEvent) {
        if (this.visionCategories?.length) {
            SortUtilities.sequenceNumberFieldInArray(this.visionCategories, "ordinal"); // need this as ordinal wasn't used previously
            SortUtilities.reorderItemInIntegerSortedArray(this.visionCategories, "ordinal", e.fromIndex, e.toIndex);
            this.visionCategories.sort(SortUtilities.getSortByFieldFunction((c) => c.ordinal));
        }
    }

    @Autobind
    public editVisionCategory(category: VisionCategory) {
        const op = category.entityAspect.entityState.isAdded() ? "Add" : "Edit";
        if (!category.description && this.descriptionSlugs[category.descriptionSlug]) {
            category.description = this.descriptionSlugs[category.descriptionSlug];
        }
        const dialogData: IEditSimpleValueBreezeEntityDialogData = {
            title: `${op} vision category`,
            entities: [{
                label: "Name",
                entity: category,
                fieldName: "name",
                type: SimpleValueType.Text,
            }, {
                label: "Description",
                entity: category,
                fieldName: "description",
                type: SimpleValueType.RichText,
            }],
            dialogWidth: 500,
            saveOnClose: true,
        };
        return this.dialogService.open(EditSimpleValueBreezeEntityDialogComponent, dialogData).pipe(
            tap(() => this.categoryCollapsed[category.visionCategoryId] = false), // expand to show changes
            // return just the vision category - both fields from the dialog are from the same entity
            map(() => category),
        );
    }

    @Autobind
    public addVisionCategory() {
        return this.visionService.createVisionCategoryForTeam(this.team).pipe(
            switchMap((newCategory) => {
                const ordinals = this.visionCategories?.map((c) => c.ordinal) || [];
                newCategory.ordinal = ordinals.length > 0 ? Math.max(...ordinals) + 1 : 1;
                return this.editVisionCategory(newCategory);
            }),
            // would have been saved by now from the EditSimpleValueBreezeEntityDialog - won't emit here if cancelled
            tap((newCategory) => this.visionCategories!.push(newCategory)),
            // only do this if there are already visions for the team - otherwise they will be created when capturing visions for the team
            switchMap((newCategory) => this.visionService.hasVisionsForTeam(this.team.teamId).pipe(
                filter((hasVisions) => hasVisions),
                map(() => newCategory),
            )),
            switchMap((newCategory) => this.visionService.createVisionsForTeamForNewCategory(this.team, newCategory)),
            switchMap((visions) => this.visionService.saveEntities(visions)),
            tap(() => this.scrollIntoView("end")),
        );
    }

    @Autobind
    public deleteVisionCategory(category: VisionCategory) {
        return this.dialogService.openConfirmationDialog({
            title: "Deleting vision category",
            message: `<p>Are you sure you want to delete the vision category "<b>${category.name}</b>"?</p>
            <p>This will also delete all associated team vision statements for this category.</p>`,
            checkboxMessage: "Check here to confirm that this deletion cannot be undone.",
            confirmButtonText: "Delete",
        } as IConfirmationDialogData).pipe(
            // server is already handling the vision entities deletion - don't have to do it here anymore
            switchMap(() => this.visionService.remove(category)),
            switchMap(() => this.visionService.saveEntities(category)),
            tap(() => this.visionCategories = this.visionCategories?.filter((c) => c.visionCategoryId !== category.visionCategoryId)),
        );
    }
}
